import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import {
  PageShell, TabBar, DataTable, StatusPill, Stat, Pill, Icon,
  LoadingBox, ErrorBox, InfiniteSentinel,
  useAdminApi, useInfiniteList, fmtDate, fmtBytes, fmtCurrency, fmtDateShort,
  ActionButton, IconButton, Field, Modal, useConfirmDialog,
  DateCell, DeviceBar, Hint,
} from "./_adminApi";

/**
 * Everything about one licence, in one place.
 *
 * Top: breadcrumb (Licences / {email}) + header (email, store, status pill)
 * Stat strip: device count, event count, bug reports, payments, backup?
 * Tabs: Overview · Devices · Events · Payments · Backups · Audit · Bugs
 *
 * Each sub-tab hits a nested endpoint under /api/admin/licences/<id>/… so
 * we never load more than one bucket at a time. Tab state in URL hash so
 * browser back/forward works.
 */
export default function LicenceDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAdminApi();
  const activeTab = (location.hash || "#overview").replace(/^#/, "");
  const setTab = (t) => navigate({ hash: `#${t}` }, { replace: true });
  const [confirm, confirmModal] = useConfirmDialog();

  const [licence, setLicence] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    setErr(null);
    api.get(`admin/licences/${id}/`).then((r) => setLicence(r.data)).catch(setErr);
  };
  useEffect(load, [id]);

  const rotateBackupSecret = async () => {
    const ok = await confirm({
      title: "Rotate backup secret?",
      body: `A new backup_secret will be generated for this licence.\n\n⚠️ Every encrypted backup already uploaded for this licence becomes UNRESTORABLE.`,
      detail: "The next successful backup from the client will use the new secret. Only do this if the current secret is compromised.",
      confirmLabel: "Rotate secret",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`admin/licences/${id}/rotate-backup-secret/`);
      alert("Backup secret rotated. The client's next backup will use the new key.");
      load();
    } catch (e) { alert(e?.response?.data?.error || e.message || "Rotation failed."); }
  };

  const deleteLicence = async () => {
    const ok = await confirm({
      title: "Delete licence?",
      body: `Permanently delete this licence (${licence.email || licence.id})?`,
      detail: `This will CASCADE delete all ${licence.device_count} devices, ${licence.event_count} events, ${licence.payment_count} payments, and the backup metadata row. Bug reports are kept but unlinked. The S3 backup blob is NOT deleted.`,
      confirmLabel: "Delete licence forever",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/licences/${id}/`);
      navigate("/dashboard/admin/licences");
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Delete failed."); }
  };

  if (err) return <PageShell title="Licence"><ErrorBox error={err} /></PageShell>;
  if (!licence) return <PageShell title="Licence"><LoadingBox /></PageShell>;

  const tenantLabel = licence.tenant?.hospital_name
    || licence.tenant?.lab_name
    || licence.tenant?.store_name
    || licence.store_name || "(unnamed)";

  return (
    <PageShell
      breadcrumb={[
        { label: "Admin", to: "#" },
        { label: "Licences", to: "/dashboard/admin/licences" },
        { label: licence.email || `#${licence.id.slice(0, 8)}` },
      ]}
      title={<>{licence.email || "(no email)"} <StatusPill status={licence.licence_status} /></>}
      subtitle={tenantLabel + (licence.store_city ? ` · ${licence.store_city}` : "")}
      actions={
        <>
          <ActionButton variant="ghost" onClick={rotateBackupSecret} icon={<Icon.key />}
            title="Generate a new backup_secret — invalidates existing backups">
            Rotate backup key
          </ActionButton>
          <ActionButton variant="danger" onClick={deleteLicence} icon={<Icon.trash />}
            title="Permanently delete this licence and all its data">
            Delete licence
          </ActionButton>
        </>
      }
    >
      {/* Stat strip — clickable, jumps to the matching tab */}
      <div className="ad-stats">
        <Stat tone="teal"   icon={Icon.monitor}  label="Devices"
          value={`${licence.active_device_count ?? 0}`}
          sub={`/ ${licence.max_devices ?? 1} allowed`}
          onClick={() => setTab("devices")} />
        <Stat tone="blue"   icon={Icon.activity} label="Events"
          value={licence.event_count ?? 0} onClick={() => setTab("events")} />
        <Stat tone="green"  icon={Icon.wallet}   label="Payments"
          value={licence.payment_count ?? 0} onClick={() => setTab("payments")} />
        <Stat tone="red"    icon={Icon.bug}      label="Bug reports"
          value={licence.bug_report_count ?? 0} onClick={() => setTab("bug-reports")} />
        <Stat tone="violet" icon={Icon.cloud}    label="Backup"
          value={licence.has_backup ? "Present" : "None"} onClick={() => setTab("backups")} />
      </div>

      <TabBar
        active={activeTab}
        onChange={setTab}
        tabs={[
          { key: "overview", label: "Overview" },
          { key: "devices", label: "Devices", count: licence.device_count },
          { key: "events", label: "Events", count: licence.event_count },
          { key: "payments", label: "Payments", count: licence.payment_count },
          { key: "backups", label: "Backups" },
          { key: "audit-logs", label: "Audit" },
          { key: "app-logs", label: "App Logs", count: licence.app_log_count },
          { key: "bug-reports", label: "Bugs", count: licence.bug_report_count },
        ]}
      />

      {activeTab === "overview" && <OverviewTab licence={licence} onChanged={load} />}
      {activeTab === "devices" && <DevicesTab licenceId={licence.id} />}
      {activeTab === "events" && <EventsTab licenceId={licence.id} />}
      {activeTab === "payments" && <PaymentsTab licenceId={licence.id} />}
      {activeTab === "backups" && <BackupsTab licenceId={licence.id} />}
      {activeTab === "audit-logs" && <AuditTab licenceId={licence.id} />}
      {activeTab === "app-logs" && <AppLogsTab licenceId={licence.id} />}
      {activeTab === "bug-reports" && <BugsTab licenceId={licence.id} />}
      {confirmModal}
    </PageShell>
  );
}

// ─── Overview — editable core fields ────────────────────────────

function OverviewTab({ licence, onChanged }) {
  const api = useAdminApi();
  const [status, setStatus] = useState(licence.licence_status);
  const [maxDev, setMaxDev] = useState(licence.max_devices);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`admin/licences/${licence.id}/`, {
        licence_status: status,
        max_devices: Number(maxDev) || 1,
      });
      onChanged();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="ad-grid2">
      <div className="ad-card ad-sect">
        <h3><span className="ic"><Icon.shield /></span>Licence settings</h3>
        <dl className="ad-dl">
          <dt>Licence ID <Hint text="UUID auto-generated on create. Never changes." /></dt>
          <dd><span className="mono" title={licence.id}>{String(licence.id).slice(0, 8)}…</span></dd>

          <dt>Email</dt>
          <dd>{licence.email || "—"}</dd>

          <dt>Status <Hint text="active = app validates normally. flagged = app still works but needs review. revoked = app rejects validation — client sees 'invalid licence'." /></dt>
          <dd>
            <select className="inline-ctl auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="flagged">Flagged</option>
              <option value="revoked">Revoked</option>
            </select>
          </dd>

          <dt>Max devices <Hint text="How many separate installs can validate at once. Raising it allows more; lowering below the current active count flags excess devices." /></dt>
          <dd><input type="number" min="1" max="20" value={maxDev} onChange={(e) => setMaxDev(e.target.value)} className="inline-ctl" /></dd>

          <dt>Created</dt>
          <dd><DateCell iso={licence.created_at} /></dd>

          <dt>Backup secret <Hint text="Auto-generated 64-char hex. Desktop app uses this to derive the AES-256 key for encrypted backups. Rotate from the top-right button if compromised." /></dt>
          <dd><span className="mono">{licence.backup_secret?.slice(0, 16)}…</span></dd>
        </dl>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <ActionButton onClick={save} disabled={saving} icon={<Icon.check />}>
            {saving ? "Saving…" : "Save changes"}
          </ActionButton>
        </div>
      </div>

      <div className="ad-card ad-sect">
        <h3><span className="ic"><Icon.hospital /></span>Store / tenant</h3>
        <dl className="ad-dl">
          <dt>Store name</dt><dd>{licence.store_name || "—"}</dd>
          <dt>Store type</dt><dd>{licence.store_type || "—"}</dd>
          <dt>City</dt><dd>{licence.store_city || "—"}</dd>
          <dt>State</dt><dd>{licence.store_state || "—"}</dd>
          <dt>Address</dt><dd style={{ textAlign: "right" }}>{licence.store_address || "—"}</dd>
          <dt>Phone</dt><dd>{licence.store_phone_number || "—"}</dd>
          <dt>Modules</dt>
          <dd>
            {licence.is_hospital && <Pill kind="bad">Hospital</Pill>}{" "}
            {licence.is_store && <Pill kind="teal">Store</Pill>}{" "}
            {licence.is_lab && <Pill kind="blue">Lab</Pill>}{" "}
            {licence.is_radiology_lab && <Pill kind="violet">Radiology</Pill>}
            {!licence.is_hospital && !licence.is_store && !licence.is_lab && !licence.is_radiology_lab && "—"}
          </dd>

          {licence.tenant && (
            <>
              <div className="grp">Tenant lock</div>
              <dt>Hospital name</dt><dd>{licence.tenant.hospital_name || "—"}</dd>
              <dt>Hospital Reg #</dt><dd>{licence.tenant.hospital_reg_no || "—"}</dd>
              <dt>Lab name</dt><dd>{licence.tenant.lab_name || "—"}</dd>
              <dt>Lab Reg #</dt><dd>{licence.tenant.lab_reg_no || "—"}</dd>
              <dt>NTN</dt><dd>{licence.tenant.store_ntn || "—"}</dd>
              <dt>Locked at</dt><dd>{licence.tenant.locked_at ? <DateCell iso={licence.tenant.locked_at} /> : "—"}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

// ─── Devices (nested) ───────────────────────────────────────────

function DevicesTab({ licenceId }) {
  const api = useAdminApi();
  const [nonce, setNonce] = useState(0);
  const [confirm, confirmModal] = useConfirmDialog();
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/licences/${licenceId}/devices/?_=${nonce}`);

  const flip = async (dev, newStatus) => {
    const ok = await confirm({
      title: `${newStatus === "revoked" ? "Revoke" : "Re-enable"} device?`,
      body: newStatus === "revoked"
        ? `The desktop app on ${dev.hostname || "this device"} will stop receiving licence validation. The user sees "licence invalid" on next heartbeat.`
        : `${dev.hostname || "This device"} will start passing licence validation again.`,
      confirmLabel: newStatus === "revoked" ? "Revoke" : "Re-enable",
      destructive: newStatus === "revoked",
    });
    if (!ok) return;
    try {
      await api.patch(`admin/devices/${dev.id}/`, { status: newStatus });
      setNonce((n) => n + 1);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const del = async (dev) => {
    const ok = await confirm({
      title: "Delete device?",
      body: `Permanently remove device ${dev.hostname || dev.install_id?.slice(0, 10)} from the DB. The licence seat is freed.`,
      detail: "Prefer 'Revoke' if you want to keep the audit trail. Delete is irreversible.",
      confirmLabel: "Delete device",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/devices/${dev.id}/`);
      setNonce((n) => n + 1);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  if (err) return <ErrorBox error={err} />;
  if (!initialized) return <LoadingBox />;
  return (
    <div className="ad-card">
    <DataTable
      rows={items}
      emptyText="No devices activated on this licence."
      columns={[
        { label: "Hostname / OS", render: (d) => (
          <div>
            <div className="t-strong" style={{ fontWeight: 600 }}>{d.hostname || "—"}</div>
            <div className="t-sub">{d.os_name || ""}</div>
          </div>
        ) },
        { label: "App", render: (d) => <span className="mono">{d.app_version || "—"}</span> },
        { label: "Install ID", render: (d) => <span className="mono t-muted">{(d.install_id || "").slice(0, 10)}…</span> },
        { label: "First seen", render: (d) => <DateCell iso={d.first_seen} /> },
        { label: "Last seen", render: (d) => <DateCell iso={d.last_seen} /> },
        { label: "Status", render: (d) => <StatusPill status={d.status} /> },
        {
          label: "Actions", align: "right",
          render: (d) => (
            <div className="ad-actions">
              {d.status !== "revoked" ? (
                <ActionButton variant="ghost" size="sm" onClick={() => flip(d, "revoked")}
                  title="Mark as revoked — keeps audit, blocks validation">
                  Revoke
                </ActionButton>
              ) : (
                <ActionButton variant="ghost" size="sm" onClick={() => flip(d, "active")}
                  title="Re-enable this device">
                  Re-enable
                </ActionButton>
              )}
              <IconButton tone="danger" onClick={() => del(d)} title="Permanently delete the device row"><Icon.trash /></IconButton>
            </div>
          ),
        },
      ]}
    />
    <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
    {confirmModal}
    </div>
  );
}

// ─── Events (nested) ────────────────────────────────────────────

function EventsTab({ licenceId }) {
  const [typeFilter, setTypeFilter] = useState("");
  const url = `admin/licences/${licenceId}/events/${typeFilter ? `?event_type=${typeFilter}` : ""}`;
  const { items, loadMore, loading, err, hasMore, initialized } = useInfiniteList(url);

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div className="ad-tools" style={{ flex: 1 }}>
          <div className="ad-select">
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All event types</option>
              <option value="activate">activate</option>
              <option value="validate">validate</option>
              <option value="heartbeat">heartbeat</option>
              <option value="conflict_install">conflict_install</option>
              <option value="conflict_fingerprint">conflict_fingerprint</option>
              <option value="conflict_tenant">conflict_tenant</option>
              <option value="rebind">rebind</option>
              <option value="revoke">revoke</option>
              <option value="legacy_validate">legacy_validate</option>
            </select>
          </div>
        </div>
      </div>
      {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
      {!initialized && !err ? <LoadingBox /> : initialized && (
        <>
          <DataTable
            rows={items}
            emptyText="No events for this licence yet."
            columns={[
              { label: "When", render: (e) => <DateCell iso={e.created_at} /> },
              { label: "Type", render: (e) => <Pill kind={
                e.event_type === "validate" || e.event_type === "activate" || e.event_type === "heartbeat" ? "teal" :
                e.event_type?.startsWith("conflict") ? "warn" :
                e.event_type === "revoke" ? "bad" : "neutral"
              }>{e.event_type}</Pill> },
              { label: "Install", render: (e) => <span className="mono t-muted">{(e.install_id || "").slice(0, 10)}…</span> },
              { label: "IP", render: (e) => <span className="mono">{e.ip || "—"}</span> },
              { label: "Details", render: (e) => (
                <pre style={{ fontSize: 11.5, color: "var(--fg-3)", whiteSpace: "pre-wrap", maxWidth: 360, maxHeight: 60, overflow: "hidden", margin: 0, fontFamily: "var(--font-mono)" }}>
                  {Object.keys(e.details || {}).length ? JSON.stringify(e.details, null, 1) : "—"}
                </pre>
              ) },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}

// ─── Payments (nested, with Add Payment) ────────────────────────

function PaymentsTab({ licenceId }) {
  const api = useAdminApi();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [confirm, confirmModal] = useConfirmDialog();
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/licences/${licenceId}/payments/?_=${nonce}`);

  const del = async (p) => {
    const ok = await confirm({
      title: "Delete payment?",
      body: `Permanently remove the ${fmtCurrency(p.amount)} payment recorded on ${fmtDateShort(p.created_at)}?`,
      detail: "Use Edit if the amount or date is wrong. Delete is for mistaken rows only — the licence stays active.",
      confirmLabel: "Delete payment",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/payments/${p.id}/`);
      setNonce((n) => n + 1);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  if (err) return <ErrorBox error={err} />;
  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div style={{ color: "var(--fg-3)", fontSize: 13, fontWeight: 500 }}>Payment history for this licence</div>
        <ActionButton icon={<Icon.plus />} size="sm" onClick={() => setShowAdd(true)}>Record payment</ActionButton>
      </div>
      {!initialized ? <LoadingBox /> : (
        <>
          <DataTable
            rows={items}
            emptyText="No payments recorded for this licence."
            columns={[
              { label: "Amount", render: (p) => <span className="t-strong t-num">{fmtCurrency(p.amount)}</span> },
              { label: "Last paid", render: (p) => <span className="t-num">{fmtDateShort(p.last_paid_date)}</span> },
              { label: "Next due", render: (p) => {
                const overdue = p.next_collection_date && new Date(p.next_collection_date) < new Date();
                return overdue
                  ? <span style={{ color: "var(--danger)", fontWeight: 700 }} title="Overdue">{fmtDateShort(p.next_collection_date)}</span>
                  : <span className="t-num">{fmtDateShort(p.next_collection_date)}</span>;
              } },
              { label: "Notes", render: (p) => <span className="t-sub" style={{ maxWidth: 320, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.notes || "—"}</span> },
              { label: "Recorded", render: (p) => <DateCell iso={p.created_at} /> },
              { label: "Actions", align: "right", render: (p) => (
                <div className="ad-actions">
                  <IconButton onClick={() => setEditing(p)} title="Edit this payment"><Icon.edit /></IconButton>
                  <IconButton tone="danger" onClick={() => del(p)} title="Delete this payment"><Icon.trash /></IconButton>
                </div>
              ) },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
      {showAdd && <PaymentModal licenceId={licenceId} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); setNonce((n) => n + 1); }} />}
      {editing && <PaymentModal licenceId={licenceId} payment={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); setNonce((n) => n + 1); }} />}
      {confirmModal}
    </div>
  );
}

/** Create OR edit payment, depending on `payment` prop. */
function PaymentModal({ licenceId, payment, onClose, onDone }) {
  const api = useAdminApi();
  const isEdit = !!payment;
  const [amount, setAmount] = useState(payment?.amount || "");
  const [lastPaid, setLastPaid] = useState(payment?.last_paid_date || new Date().toISOString().slice(0, 10));
  const [nextDue, setNextDue] = useState(payment?.next_collection_date || "");
  const [notes, setNotes] = useState(payment?.notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null); setSaving(true);
    try {
      const body = {
        amount: Number(amount),
        last_paid_date: lastPaid || null,
        next_collection_date: nextDue || null,
        notes: notes.trim(),
      };
      if (isEdit) await api.patch(`admin/payments/${payment.id}/`, body);
      else await api.post(`admin/licences/${licenceId}/payments/`, body);
      onDone();
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  return (
    <Modal
      title={isEdit ? "Edit payment" : "Record a payment"}
      icon={Icon.wallet}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button className="ad-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ad-btn primary" onClick={submit} disabled={saving || !amount}>
            <Icon.check />{saving ? "Saving…" : (isEdit ? "Save changes" : "Record")}
          </button>
        </>
      }
    >
      {err && <ErrorBox error={err} />}
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Amount (PKR)" hint="How much was received" full>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="form-control" required autoFocus />
          </Field>
          <Field label="Last paid" hint="Date the customer paid">
            <input type="date" value={lastPaid} onChange={(e) => setLastPaid(e.target.value)} className="form-control" />
          </Field>
          <Field label="Next due" hint="When next payment is expected">
            <input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} className="form-control" />
          </Field>
          <Field label="Notes" hint="Reference number, payment method, part-payment reason" full>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="form-control" />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// ─── Backups (nested) ───────────────────────────────────────────

function BackupsTab({ licenceId }) {
  const api = useAdminApi();
  const [nonce, setNonce] = useState(0);
  const [confirm, confirmModal] = useConfirmDialog();
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/licences/${licenceId}/backups/?_=${nonce}`);

  const download = async (b) => {
    try {
      const res = await api.get(`admin/backups/${b.id}/`);
      const url = res.data?.download_url;
      if (!url) return alert("Server did not return a download URL.");
      window.open(url, "_blank");
    } catch (e) { alert(e?.response?.data?.error || e.message || "Download failed."); }
  };

  const del = async (b) => {
    const ok = await confirm({
      title: "Delete backup?",
      body: `Permanently delete this backup?`,
      detail: `Both the DB row AND the S3 blob (${fmtBytes(b.size_bytes)}) are removed. The client has nothing to restore from until the next successful backup from their machine.`,
      confirmLabel: "Delete backup",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/backups/${b.id}/`);
      setNonce((n) => n + 1);
    } catch (e) { alert(e?.response?.data?.error || e.message); }
  };

  if (err) return <ErrorBox error={err} />;
  if (!initialized) return <LoadingBox />;
  return (
    <div className="ad-card">
      <DataTable
        rows={items}
        emptyText="No backups uploaded for this licence yet."
        columns={[
          { label: "Status", render: (b) => (
            <span title={b.status === "ready" ? "Upload complete, SHA verified. Restorable." : "Upload in progress or incomplete."}>
              <StatusPill status={b.status} />
            </span>
          ) },
          { label: "Size", align: "right", render: (b) => <span className="t-num">{fmtBytes(b.size_bytes)}</span> },
          { label: "Schema", render: (b) => <span className="mono" title="DB schema version — must match the client's app version for restore to succeed">{b.schema_version || "—"}</span> },
          { label: "Client", render: (b) => <span className="mono" title="Desktop app version that uploaded this backup">{b.client_version || "—"}</span> },
          { label: "Uploaded", render: (b) => <DateCell iso={b.updated_at} /> },
          { label: "S3 key", render: (b) => <span className="code-pill" title={b.s3_key} style={{ maxWidth: 220 }}>{b.s3_key}</span> },
          { label: "Actions", align: "right", render: (b) => (
            <div className="ad-actions">
              <IconButton tone="brand" onClick={() => download(b)} title="Open encrypted blob for forensic / manual restore"><Icon.download /></IconButton>
              <IconButton tone="danger" onClick={() => del(b)} title="Delete from S3 + DB"><Icon.trash /></IconButton>
            </div>
          ) },
        ]}
      />
      <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
      {confirmModal}
    </div>
  );
}

// ─── Audit log (nested) ─────────────────────────────────────────

function AuditTab({ licenceId }) {
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/licences/${licenceId}/audit-logs/`);

  if (err) return <ErrorBox error={err} />;
  if (!initialized) return <LoadingBox />;
  return (
    <div className="ad-card">
      <DataTable
        rows={items}
        emptyText="No audit entries from this licence."
        columns={[
          { label: "When", render: (r) => <DateCell iso={r.timestamp} /> },
          { label: "Action", render: (r) => <Pill kind="teal">{r.action}</Pill> },
          { label: "Endpoint", render: (r) => <span className="code-pill">{r.endpoint || "—"}</span> },
        ]}
      />
      <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
    </div>
  );
}

// ─── App logs (nested) — forwarded console lines from the user's desktop ──

function AppLogsTab({ licenceId }) {
  const [level, setLevel] = useState("");
  const [q, setQ] = useState("");
  const params = new URLSearchParams();
  if (level) params.set("level", level);
  if (q) params.set("q", q);
  const url = `admin/licences/${licenceId}/app-logs/${params.toString() ? `?${params}` : ""}`;
  const { items, loadMore, loading, err, hasMore, initialized } = useInfiniteList(url);

  const levelColour = (lvl) => ({
    info: "#2563eb",
    warn: "#b45309",
    error: "#b91c1c",
  }[lvl] || "#6b7280");

  if (err) return <ErrorBox error={err} />;
  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div className="ad-tools" style={{ flex: 1 }}>
          <label className="ad-search">
            <Icon.search />
            <input placeholder="Search message…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="ad-select">
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="">All levels</option>
              <option value="error">Errors only</option>
              <option value="warn">Warnings</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>
      </div>
      {!initialized ? <LoadingBox /> : (
        <>
          <DataTable
            rows={items}
            emptyText="No log lines forwarded from this user's app yet."
            columns={[
              { label: "When", render: (r) => <DateCell iso={r.occurred_at || r.created_at} /> },
              { label: "Level", render: (r) => (
                <Pill kind={r.level === "error" ? "bad" : r.level === "warn" ? "warn" : "blue"}>{r.level}</Pill>
              ) },
              { label: "Source", render: (r) => <span className="mono">{r.source || "—"}</span> },
              { label: "Message", render: (r) => (
                <pre style={{ fontSize: 12, color: "var(--fg-1)", whiteSpace: "pre-wrap", maxWidth: 480, maxHeight: 120, overflow: "auto", margin: 0, fontFamily: "var(--font-mono)" }}>
                  {r.message}
                </pre>
              ) },
              { label: "App", render: (r) => <span className="mono t-muted">{r.app_version || "—"}</span> },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}

// ─── Bug reports (nested — reuses flat endpoint, filters by licence) ──

function BugsTab({ licenceId }) {
  const navigate = useNavigate();
  // Reuse the flat endpoint; filter client-side by licence_id since
  // BugReport has no admin per-licence nested URL. Infinite scroll still
  // applies — we keep loading pages until none are left or we run out.
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/bug-reports/`);
  const filtered = items.filter((b) => String(b.licence_id) === String(licenceId));

  if (err) return <ErrorBox error={err} />;
  if (!initialized) return <LoadingBox />;
  return (
    <div className="ad-card">
      <DataTable
        rows={filtered}
        emptyText="No bug reports from this licence."
        onRowClick={(r) => navigate(`/dashboard/admin/bug-reports/${r.id}`)}
        columns={[
          { label: "#", render: (r) => <span className="t-muted mono">#{r.id}</span> },
          { label: "Status", render: (r) => <StatusPill status={r.status} /> },
          { label: "Title", render: (r) => <span className="t-strong">{r.title || "(no title)"}</span> },
          { label: "Reporter", render: (r) => <span className="t-sub">{r.reporter_email || "—"}</span> },
          { label: "App", render: (r) => <span className="mono">{r.app_version || "—"}</span> },
          { label: "Received", render: (r) => <DateCell iso={r.created_at} /> },
        ]}
      />
      <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
    </div>
  );
}
